import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Sun } from 'lucide-react';
import { getDaylightRange } from '../../scene3d/sunlight';

/**
 * SunTimeControls - Zeitregler für den Sonnenstand (#604)
 *
 * Geteilte, WebGL-freie Steuerung für 3D (jetzt) und 2D-Overlays (#605):
 * Datum + Uhrzeit bestimmen den Sonnenstand. Der Uhrzeit-Slider ist auf
 * Sonnenauf- bis -untergang des gewählten Tages begrenzt — außerhalb steht
 * die Sonne unter dem Horizont, dort gibt es nichts zu sehen. Presets
 * springen auf die Sonnenwenden/Tagundnachtgleiche (21. Mär/Jun/Dez).
 *
 * Bewusst ohne Play-Animation: das Ziehen des Sliders *ist* die Bewegung
 * (ruhige Gestaltung, Markenton).
 *
 * Controlled: der Aufrufer hält `date` (Tag + Uhrzeit) und bekommt bei jeder
 * Änderung eine neue Date-Instanz — mit auf den Tageslichtbereich geklemmter
 * Uhrzeit, damit die Szene nie in die Nacht springt.
 */

const DEFAULT_RANGE = { start: 6 * 60, end: 20 * 60 }; // Fallback bei Polartag/-nacht
const SLIDER_STEP_MIN = 5;

const PRESETS = [
  { id: 'spring', label: '21. Mär', month: 2, day: 21, title: 'Frühlingsanfang (21. März)' },
  { id: 'summer', label: '21. Jun', month: 5, day: 21, title: 'Sommeranfang (21. Juni)' },
  { id: 'winter', label: '21. Dez', month: 11, day: 21, title: 'Winteranfang (21. Dezember)' },
];

const pad2 = (n) => String(n).padStart(2, '0');
const toMinutes = (d) => d.getHours() * 60 + d.getMinutes();
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
const formatTime = (min) => `${pad2(Math.floor(min / 60))}:${pad2(min % 60)}`;
const toDateInputValue = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** Tageslicht-Minutenfenster [lo, hi] eines Tages, mit Polar-Fallback. */
function daylightMinutes(latitude, longitude, date) {
  const { sunrise, sunset } = getDaylightRange(latitude, longitude, date);
  const start = sunrise ? toMinutes(sunrise) : DEFAULT_RANGE.start;
  const end = sunset ? toMinutes(sunset) : DEFAULT_RANGE.end;
  return { lo: Math.min(start, end), hi: Math.max(start, end) };
}

const SunTimeControls = ({ date, latitude, longitude, onChange, className }) => {
  // Fenster nur bei Tageswechsel/Ort neu berechnen (SunCalc pro Render sparen)
  const dayKey = toDateInputValue(date);
  const { lo, hi } = useMemo(
    () => daylightMinutes(latitude, longitude, date),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dayKey deckt den Tag ab
    [latitude, longitude, dayKey]
  );

  const currentMin = clamp(toMinutes(date), lo, hi);

  // Emittiert ein neues Datum mit in den Tageslichtbereich geklemmter Uhrzeit.
  const emit = (nextDate) => {
    const { lo: nLo, hi: nHi } = daylightMinutes(latitude, longitude, nextDate);
    const min = clamp(toMinutes(nextDate), nLo, nHi);
    const out = new Date(nextDate);
    out.setHours(Math.floor(min / 60), min % 60, 0, 0);
    onChange(out);
  };

  const handleTimeChange = (e) => {
    const min = Number(e.target.value);
    const out = new Date(date);
    out.setHours(Math.floor(min / 60), min % 60, 0, 0);
    onChange(out);
  };

  const handleDateChange = (e) => {
    const value = e.target.value;
    if (!value) return;
    const [y, m, d] = value.split('-').map(Number);
    const next = new Date(date);
    next.setFullYear(y, m - 1, d);
    emit(next);
  };

  const applyPreset = (preset) => {
    const next = new Date(date);
    next.setMonth(preset.month, preset.day);
    next.setHours(12, 0, 0, 0);
    emit(next);
  };

  const isPresetActive = (preset) =>
    date.getMonth() === preset.month && date.getDate() === preset.day;

  return (
    <div
      className={`rounded-lg bg-white/90 backdrop-blur-sm border border-gray-200 shadow-sm p-3 ${className}`}
    >
      <div className="flex items-center gap-2">
        <Sun className="h-4 w-4 text-amber-500" aria-hidden="true" />
        <span className="text-sm font-medium text-gray-700">Sonnenstand</span>
        <span className="ml-auto tabular-nums text-sm font-semibold text-gray-900">
          {formatTime(currentMin)}
        </span>
      </div>

      {/* Uhrzeit-Slider (Sonnenauf- bis -untergang) */}
      <div className="mt-2">
        <input
          type="range"
          min={lo}
          max={hi}
          step={SLIDER_STEP_MIN}
          value={currentMin}
          onChange={handleTimeChange}
          aria-label="Uhrzeit"
          aria-valuetext={formatTime(currentMin)}
          className="w-full accent-amber-500"
        />
        <div className="mt-0.5 flex justify-between text-xs text-gray-400 tabular-nums">
          <span>{formatTime(lo)}</span>
          <span>{formatTime(hi)}</span>
        </div>
      </div>

      {/* Datum + Saison-Presets */}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor="sun-date">
          Datum
        </label>
        <input
          id="sun-date"
          type="date"
          value={toDateInputValue(date)}
          onChange={handleDateChange}
          className="min-h-[44px] rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
        />
        <div role="group" aria-label="Jahreszeit" className="inline-flex items-center gap-1">
          {PRESETS.map((preset) => {
            const active = isPresetActive(preset);
            return (
              <button
                key={preset.id}
                type="button"
                aria-pressed={active}
                title={preset.title}
                onClick={() => applyPreset(preset)}
                className={`min-h-[44px] rounded-md px-3 py-1 text-sm font-medium transition-colors ${
                  active
                    ? 'bg-emerald-50 text-emerald-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {preset.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

SunTimeControls.propTypes = {
  date: PropTypes.instanceOf(Date).isRequired,
  latitude: PropTypes.number.isRequired,
  longitude: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
};

SunTimeControls.defaultProps = {
  className: '',
};

export default SunTimeControls;
